import { ChangeDetectionStrategy, Component, ElementRef, OnInit, ViewChild, computed, effect, inject, signal } from "@angular/core";
import { Router } from "@angular/router";
import { OidcService, AuthRedirectError, logger } from '@fry-it/risr-core';
import { ClusterService } from '@fry-it/risr-core';
import { OrganisationService } from '@fry-it/risr-core';
import { FeaturesService } from '@fry-it/risr-core';
import { SecurityService } from '@fry-it/risr-core';
import { SYSTEM_ROLE_ID } from '@fry-it/risr-core';
import { type RisrAiContext } from '@fry-it/risr-ai';
import { RouterEventsService } from './upgrade/router.events.service';
import { UserProfileService } from './organisation/user/user-profile/user-profile.service';


@Component({
    selector: "app-main",
    templateUrl: './main.component.html',
    styles: [],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MainComponent implements OnInit {

  @ViewChild('kaizen') kaizenEl: ElementRef;

  bootstraped = signal(false);

  private oidc = inject(OidcService);
  private cluster = inject(ClusterService);
  private organisation = inject(OrganisationService);
  private router = inject(Router);
  private routerEvents = inject(RouterEventsService);
  private userProfile = inject(UserProfileService);
  private features = inject(FeaturesService);
  private security = inject(SecurityService);

  // Whether the current user holds the `aiassistant.use` permission.
  private aiUsePermitted = signal(false);

  // Whether the current user holds the fry (system:superadmin) role.
  private isSuperAdmin = signal(false);

  // Whether the "ai-assistant" feature flag is enabled for the current org.
  aiAssistantEnabled = computed(() =>
    this.features.enabledFeatures().some(f => f.id === 'ai-assistant')
  );

  // The assistant renders for permitted users when the org feature flag is on;
  // superadmins bypass the flag so they can always access it.
  showAiAssistant = computed(() =>
    (this.aiAssistantEnabled() || this.isSuperAdmin()) && this.aiUsePermitted()
  );

  // Display name of the currently viewed subject user, resolved asynchronously.
  private subjectName = signal<string | null>(null);

  /**
   * Page context fed into the shared `<risr-ai-assistant-host>`. Token, org and
   * backend URL wiring live in that shared component; here we only describe the
   * page and the currently viewed subject user. `remoteUser` is the deepest
   * route's `user` param, or null on the signed-in user's own pages (in which
   * case we send no subject).
   */
  readonly aiContext = computed<RisrAiContext>(() => {
    const context: RisrAiContext = {
      page: { type: 'kaizen', title: document.title || undefined, url: this.router.url },
    };
    const subject = this.routerEvents.remoteUser();
    const name = this.subjectName();
    if (subject) {
      context.subjectUser = { id: subject, ...(name ? { displayName: name } : {}) };
    }
    return context;
  });

  constructor() {
    // Resolve the subject user's display name whenever the viewed user changes.
    effect(() => {
      const subject = this.routerEvents.remoteUser();
      this.subjectName.set(null);
      if (subject) {
        void this.resolveSubjectName(subject);
      }
    });
  }

  /**
   * Look up the viewed user's display name so the assistant can address them by
   * name. Best-effort: on failure we keep the id-only context. Guards against a
   * stale response overwriting the name after the user has navigated away.
   */
  private async resolveSubjectName(id: string): Promise<void> {
    try {
      const doc = await this.userProfile.fetchByUsername(id);
      const name =
        doc?.fullname?.trim() ||
        [doc?.firstName, doc?.lastName].filter(Boolean).join(' ').trim() ||
        null;
      if (this.routerEvents.remoteUser() === id) {
        this.subjectName.set(name);
      }
    } catch {
      // Leave the name unset; id-only context still lets the backend resolve the user.
    }
  }

  async ngOnInit() {
    try {
      await this.oidc.init({ relogin: true })
      await this.cluster.loadProfileOrganisations();
      await this.organisation.fetchOrganisation();
      await this.features.ensureFeatures();
      this.aiUsePermitted.set(
        await this.security.hasPermission('aiassistant.use')
      );
      this.isSuperAdmin.set(
        await this.security.hasRole(SYSTEM_ROLE_ID.SUPER_ADMIN)
      );
      this.bootstraped.set(true);
    } catch (error) {
      if (error instanceof AuthRedirectError) {
        return;
      }
      logger.error('Bootstrap failed', error instanceof Error ? error : new Error(String(error)));
    }
  }

  skipToContent(): void {
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.focus();
    }
  }
}
